import { Button, Card, CardBody, Image, Divider } from "@heroui/react";
import { Icon } from "@iconify/react";
import { useTranslation } from "react-i18next";
import { analytics } from "src/infra/analytics/analytics";
import { useStore } from "src/store/store";
import { Link } from "wouter";

function CTA() {
	const { t } = useTranslation(["home"]);

	const store = useStore();

	if (!store.user) {
		return (
			<Link to="/register">
				<Button
					size="lg"
					color="warning"
					onPress={() => {
						analytics.track("CLICK_ON_START");
					}}
					variant="shadow"
					startContent={<Icon icon="lucide:file-text" width={20} />}
				>
					{t("primaryCTA")}
				</Button>
			</Link>
		);
	}

	return (
		<div className="flex flex-wrap gap-4">
			<Link to="/new_request">
				<Button
					size="lg"
					color="primary"
					onPress={() => {
						analytics.track("CLICK_ON_START");
					}}
					variant="shadow"
					startContent={<Icon icon="lucide:file-text" width={20} />}
				>
					בקשה לרישיון מלא
				</Button>
			</Link>
			<Link to="/new_recommendation">
				<Button
					size="lg"
					color="secondary"
					onPress={() => {
						analytics.track("CLICK_ON_START");
					}}
					variant="shadow"
					startContent={<Icon icon="lucide:lightbulb" width={20} />}
				>
					בקשה להמלצה בלבד
				</Button>
			</Link>
		</div>
	);
}

const UnAuthScreen = () => {
	const { t } = useTranslation(["home"]);

	return (
		<div className="min-h-screen bg-content1">
			{/* Hero Section */}
			<section className="relative">
				<div className="absolute inset-0 bg-gradient-to-r from-black/80 to-primary-900/80 z-10" />
				<div className="relative h-[500px] overflow-hidden">
					<Image
						removeWrapper
						alt="Cannabis field"
						className="w-full h-full object-cover"
						src="https://img.heroui.chat/image/landscape?w=1920&h=1080&u=cannabis"
					/>
				</div>
				<div className="absolute inset-0 flex flex-col justify-center z-20 container mx-auto px-4 md:px-8">
					<div className="max-w-3xl bg-black/40 p-6 rounded-lg backdrop-blur-sm">
						<h1 className="text-white text-4xl md:text-5xl lg:text-6xl font-bold mb-4">
							{t("home:title")}
						</h1>
						<p className="text-white text-lg md:text-xl mb-8">{t("home:description")}</p>
						<CTA />
					</div>
				</div>
			</section>

			{/* Services Section */}
			<section className="py-16 container mx-auto px-4 md:px-8">
				<div className="text-center mb-12">
					<h2 className="text-3xl md:text-4xl font-bold mb-4">{t("home:services.title")}</h2>
					<p className="text-default-600 max-w-2xl mx-auto">{t("services.description")}</p>
				</div>

				<div className="grid grid-cols-1 md:grid-cols-2 gap-8">
					<Card className="border-none shadow-md">
						<CardBody className="p-6">
							<div className="rounded-full bg-primary-100 p-3 w-14 h-14 flex items-center justify-center mb-4">
								<Icon
									icon="lucide:clipboard-check"
									width={28}
									className="text-primary-600"
								/>
							</div>
							<h3 className="text-xl font-semibold mb-2">הנפקת רישיון חדש / חידוש רישיון</h3>
							<p className="text-default-600">
								הגשת בקשה לרישיון קנאביס רפואי חדש או חידוש רישיון קיים
							</p>
						</CardBody>
					</Card>

					<Card className="border-none shadow-md">
						<CardBody className="p-6">
							<div className="rounded-full bg-secondary-100 p-3 w-14 h-14 flex items-center justify-center mb-4">
								<Icon icon="lucide:store" width={28} className="text-secondary-600" />
							</div>
							<h3 className="text-xl font-semibold mb-2">בית מרקחת</h3>
							<p className="text-default-600">
								רכישת מוצרי קנאביס רפואי איכותיים מבית המרקחת שלנו
							</p>
						</CardBody>
					</Card>

					<Card className="border-none shadow-md">
						<CardBody className="p-6">
							<div className="rounded-full bg-warning-100 p-3 w-14 h-14 flex items-center justify-center mb-4">
								<Icon icon="lucide:shopping-bag" width={28} className="text-warning-600" />
							</div>
							<h3 className="text-xl font-semibold mb-2">עולם האקססוריז</h3>
							<p className="text-default-600">
								מגוון רחב של אקססוריז ועזרים לשימוש בקנאביס רפואי
							</p>
						</CardBody>
					</Card>

					<Card className="border-none shadow-md">
						<CardBody className="p-6">
							<div className="rounded-full bg-danger-100 p-3 w-14 h-14 flex items-center justify-center mb-4">
								<Icon icon="lucide:scale" width={28} className="text-danger-600" />
							</div>
							<h3 className="text-xl font-semibold mb-2">מיצוי זכויות עם עורך דין</h3>
							<p className="text-default-600">
								ייצוג משפטי ומיצוי זכויות במקרים הקשורים לקנאביס רפואי
							</p>
						</CardBody>
					</Card>
				</div>
			</section>

			{/* Process Section */}
			<section className="py-16 bg-content2">
				<div className="container mx-auto px-4 md:px-8">
					<div className="text-center mb-12">
						<h2 className="text-3xl md:text-4xl font-bold mb-4">{t("process.title")}</h2>
						<p className="text-default-600 max-w-2xl mx-auto">
							{t("home:process.description")}
						</p>
					</div>

					<div className="grid grid-cols-1 md:grid-cols-4 gap-8">
						<div className="text-center">
							<div className="rounded-full bg-primary-600 text-white w-16 h-16 flex items-center justify-center mx-auto mb-4 text-2xl font-bold">
								1
							</div>
							<h3 className="text-xl font-semibold mb-2">{t("process.1.title")}</h3>
							<p className="text-default-600">{t("process.1.description")}</p>
						</div>

						<div className="text-center">
							<div className="rounded-full bg-primary-600 text-white w-16 h-16 flex items-center justify-center mx-auto mb-4 text-2xl font-bold">
								2
							</div>
							<h3 className="text-xl font-semibold mb-2">{t("process.2.title")}</h3>
							<p className="text-default-600">{t("process.2.description")}</p>
						</div>

						<div className="text-center">
							<div className="rounded-full bg-primary-600 text-white w-16 h-16 flex items-center justify-center mx-auto mb-4 text-2xl font-bold">
								3
							</div>
							<h3 className="text-xl font-semibold mb-2">{t("process.3.title")}</h3>
							<p className="text-default-600">{t("process.3.description")}</p>
						</div>

						<div className="text-center">
							<div className="rounded-full bg-primary-600 text-white w-16 h-16 flex items-center justify-center mx-auto mb-4 text-2xl font-bold">
								4
							</div>
							<h3 className="text-xl font-semibold mb-2">{t("process.4.title")}</h3>
							<p className="text-default-600">{t("process.4.description")}</p>
						</div>
					</div>

					<div className="mt-12 text-center">
						<CTA />
					</div>
				</div>
			</section>

			{/* Testimonials */}
			<section className="py-16 container mx-auto px-4 md:px-8">
				<div className="text-center mb-12">
					<h2 className="text-3xl md:text-4xl font-bold mb-4">Client Success Stories</h2>
					<p className="text-default-600 max-w-2xl mx-auto">
						Hear from businesses that have successfully obtained their cannabis licenses with
						our help.
					</p>
				</div>

				<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
					<Card className="border-none shadow-md">
						<CardBody className="p-6">
							<div className="flex items-center mb-4">
								<Icon icon="lucide:star" className="text-warning" width={20} />
								<Icon icon="lucide:star" className="text-warning" width={20} />
								<Icon icon="lucide:star" className="text-warning" width={20} />
								<Icon icon="lucide:star" className="text-warning" width={20} />
								<Icon icon="lucide:star" className="text-warning" width={20} />
							</div>
							<p className="text-default-600 mb-4">
								"The team made the licensing process incredibly smooth. Their expertise
								saved us months of time and potential costly mistakes."
							</p>
							<div className="flex items-center">
								<div className="w-10 h-10 rounded-full overflow-hidden mr-3">
									<Image
										removeWrapper
										alt="Client"
										className="w-full h-full object-cover"
										src="https://img.heroui.chat/image/avatar?w=100&h=100&u=1"
									/>
								</div>
								<div>
									<p className="font-semibold">Sarah Johnson</p>
									<p className="text-small text-default-500">Green Leaf Dispensary</p>
								</div>
							</div>
						</CardBody>
					</Card>

					<Card className="border-none shadow-md">
						<CardBody className="p-6">
							<div className="flex items-center mb-4">
								<Icon icon="lucide:star" className="text-warning" width={20} />
								<Icon icon="lucide:star" className="text-warning" width={20} />
								<Icon icon="lucide:star" className="text-warning" width={20} />
								<Icon icon="lucide:star" className="text-warning" width={20} />
								<Icon icon="lucide:star" className="text-warning" width={20} />
							</div>
							<p className="text-default-600 mb-4">
								"Their knowledge of regulations across different states was invaluable. We
								successfully expanded to three new locations with their help."
							</p>
							<div className="flex items-center">
								<div className="w-10 h-10 rounded-full overflow-hidden mr-3">
									<Image
										removeWrapper
										alt="Client"
										className="w-full h-full object-cover"
										src="https://img.heroui.chat/image/avatar?w=100&h=100&u=2"
									/>
								</div>
								<div>
									<p className="font-semibold">Michael Rodriguez</p>
									<p className="text-small text-default-500">Emerald Cultivation</p>
								</div>
							</div>
						</CardBody>
					</Card>

					<Card className="border-none shadow-md">
						<CardBody className="p-6">
							<div className="flex items-center mb-4">
								<Icon icon="lucide:star" className="text-warning" width={20} />
								<Icon icon="lucide:star" className="text-warning" width={20} />
								<Icon icon="lucide:star" className="text-warning" width={20} />
								<Icon icon="lucide:star" className="text-warning" width={20} />
								<Icon icon="lucide:star-half" className="text-warning" width={20} />
							</div>
							<p className="text-default-600 mb-4">
								"As a first-time business owner, the guidance I received was crucial. They
								walked me through every step of the licensing process."
							</p>
							<div className="flex items-center">
								<div className="w-10 h-10 rounded-full overflow-hidden mr-3">
									<Image
										removeWrapper
										alt="Client"
										className="w-full h-full object-cover"
										src="https://img.heroui.chat/image/avatar?w=100&h=100&u=3"
									/>
								</div>
								<div>
									<p className="font-semibold">Jennifer Lee</p>
									<p className="text-small text-default-500">Harmony Wellness</p>
								</div>
							</div>
						</CardBody>
					</Card>
				</div>
			</section>

			{/* CTA Section */}
			<section className="py-16 bg-primary-600 text-white">
				<div className="container mx-auto px-4 md:px-8">
					<div className="max-w-3xl mx-auto text-center">
						<h2 className="text-3xl md:text-4xl font-bold mb-4">
							Ready to Start Your Cannabis Business?
						</h2>
						<p className="text-white/90 text-lg mb-8">
							Our team of experts is ready to guide you through the licensing process and
							help your business thrive.
						</p>
						<div className="flex flex-wrap justify-center gap-4">
							<Button
								size="lg"
								color="warning"
								variant="shadow"
								startContent={<Icon icon="lucide:file-plus" width={20} />}
							>
								Apply Now
							</Button>
							<Button
								size="lg"
								variant="flat"
								className="bg-white/20"
								startContent={<Icon icon="lucide:phone" width={20} />}
							>
								Contact Us
							</Button>
						</div>
					</div>
				</div>
			</section>

			{/* Footer */}
			<footer className="bg-content2 py-12">
				<div className="container mx-auto px-4 md:px-8">
					<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
						<div>
							<h3 className="text-xl font-bold mb-4">Cannabis Licensing</h3>
							<p className="text-default-600 mb-4">
								Professional cannabis licensing services to help your business succeed in a
								regulated market.
							</p>
							<div className="flex space-x-4">
								<Button isIconOnly variant="light" aria-label="Facebook">
									<Icon icon="logos:facebook" width={24} />
								</Button>
								<Button isIconOnly variant="light" aria-label="Twitter">
									<Icon icon="logos:twitter" width={24} />
								</Button>
								<Button isIconOnly variant="light" aria-label="Instagram">
									<Icon icon="logos:instagram-icon" width={24} />
								</Button>
								<Button isIconOnly variant="light" aria-label="LinkedIn">
									<Icon icon="logos:linkedin-icon" width={24} />
								</Button>
							</div>
						</div>

						<div>
							<h3 className="text-lg font-semibold mb-4">Services</h3>
							<ul className="space-y-2">
								<li>
									<a href="#" className="text-default-600 hover:text-primary-600">
										License Application
									</a>
								</li>
								<li>
									<a href="#" className="text-default-600 hover:text-primary-600">
										Regulatory Compliance
									</a>
								</li>
								<li>
									<a href="#" className="text-default-600 hover:text-primary-600">
										Business Planning
									</a>
								</li>
								<li>
									<a href="#" className="text-default-600 hover:text-primary-600">
										License Renewals
									</a>
								</li>
								<li>
									<a href="#" className="text-default-600 hover:text-primary-600">
										Consulting Services
									</a>
								</li>
							</ul>
						</div>

						<div>
							<h3 className="text-lg font-semibold mb-4">Resources</h3>
							<ul className="space-y-2">
								<li>
									<a href="#" className="text-default-600 hover:text-primary-600">
										Licensing Guide
									</a>
								</li>
								<li>
									<a href="#" className="text-default-600 hover:text-primary-600">
										Regulatory Updates
									</a>
								</li>
								<li>
									<a href="#" className="text-default-600 hover:text-primary-600">
										FAQ
									</a>
								</li>
								<li>
									<a href="#" className="text-default-600 hover:text-primary-600">
										Blog
									</a>
								</li>
								<li>
									<a href="#" className="text-default-600 hover:text-primary-600">
										Case Studies
									</a>
								</li>
							</ul>
						</div>

						<div>
							<h3 className="text-lg font-semibold mb-4">Contact Us</h3>
							<ul className="space-y-2">
								<li className="flex items-start">
									<Icon
										icon="lucide:map-pin"
										className="mr-2 mt-1 text-primary-600"
										width={16}
									/>
									<span className="text-default-600">
										123 Cannabis Way, Suite 420
										<br />
										Green City, CA 90210
									</span>
								</li>
								<li className="flex items-center">
									<Icon icon="lucide:phone" className="mr-2 text-primary-600" width={16} />
									<span className="text-default-600">(555) 123-4567</span>
								</li>
								<li className="flex items-center">
									<Icon icon="lucide:mail" className="mr-2 text-primary-600" width={16} />
									<span className="text-default-600">info@cannabislicensing.com</span>
								</li>
							</ul>
						</div>
					</div>

					<Divider className="my-8" />

					<div className="flex flex-col md:flex-row justify-between items-center">
						<p className="text-default-600 text-sm mb-4 md:mb-0">
							© 2023 Cannabis Licensing Services. All rights reserved.
						</p>
						<div className="flex space-x-4">
							<a href="#" className="text-sm text-default-600 hover:text-primary-600">
								Privacy Policy
							</a>
							<a href="#" className="text-sm text-default-600 hover:text-primary-600">
								Terms of Service
							</a>
							<a href="#" className="text-sm text-default-600 hover:text-primary-600">
								Cookie Policy
							</a>
						</div>
					</div>
				</div>
			</footer>
		</div>
	);
};

export default UnAuthScreen;
